import { clamp, isMobileBreakpoint } from './utilities.js';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 48;
const MOVE_THRESHOLD = 8;
const RESET_THRESHOLD = 1.02;

class MobilePinchZoom extends HTMLElement {
  #controller = new AbortController();
  #scale = MIN_SCALE;
  #startScale = MIN_SCALE;
  #startDistance = 0;
  #translate = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #startTouch = { x: 0, y: 0 };
  #isPinching = false;
  #isPanning = false;
  #hasMoved = false;
  #lastTapTime = 0;
  #lastTapPosition = null;
  #swiper = null;
  #previousAllowTouchMove = null;

  connectedCallback() {
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('touchcancel', this.#handleTouchEnd, options);
    window.addEventListener('resize', this.#handleResize, { signal });
  }

  disconnectedCallback() {
    this.#setSwiperTouch(true);
    this.#controller.abort();
  }

  resetZoom() {
    this.#scale = MIN_SCALE;
    this.#startScale = MIN_SCALE;
    this.#startDistance = 0;
    this.#translate = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isPinching = false;
    this.#isPanning = false;
    this.#hasMoved = false;
    this.#setSwiperTouch(true);
    this.#applyTransform();
  }

  get #image() {
    return this.querySelector('img.product-media__image, img');
  }

  #handleResize = () => {
    if (!isMobileBreakpoint()) {
      this.resetZoom();
      return;
    }

    this.#constrainTranslate();
    this.#applyTransform();
  };

  #handleTouchStart = (event) => {
    if (!this.#canZoom()) return;

    if (event.touches.length === 2) {
      this.#captureGesture(event);
      this.#startPinch(event.touches[0], event.touches[1]);
      return;
    }

    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    this.#startTouch = pointFromTouch(touch);
    this.#hasMoved = false;

    if (this.#scale > MIN_SCALE) {
      this.#captureGesture(event);
      this.#startPan(touch);
    }
  };

  #handleTouchMove = (event) => {
    if (!this.#canZoom()) return;

    if (event.touches.length === 2) {
      if (!this.#isPinching) this.#startPinch(event.touches[0], event.touches[1]);
      this.#captureGesture(event);
      this.#processPinch(event.touches[0], event.touches[1]);
      return;
    }

    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    if (getDistance(pointFromTouch(touch), this.#startTouch) > MOVE_THRESHOLD) {
      this.#hasMoved = true;
    }

    if (!this.#isPanning || this.#scale <= MIN_SCALE) return;

    this.#captureGesture(event);
    this.#processPan(touch);
  };

  #handleTouchEnd = (event) => {
    if (!this.#canZoom()) return;

    if (this.#isPinching && event.touches.length === 1) {
      const touch = event.touches[0];
      if (touch && this.#scale > MIN_SCALE) {
        this.#isPinching = false;
        this.#startPan(touch);
      }
      return;
    }

    if (event.touches.length > 0) return;

    const wasGesture = this.#isPinching || this.#isPanning;
    this.#isPinching = false;
    this.#isPanning = false;

    const touch = event.changedTouches[0];
    if (this.#scale <= RESET_THRESHOLD) {
      if (!wasGesture && !this.#hasMoved && touch) {
        this.#handleDoubleTap(event, touch);
        if (this.#scale > RESET_THRESHOLD) return;
      }

      this.resetZoom();
      return;
    }

    this.#constrainTranslate();
    this.#applyTransform();

    if (wasGesture || this.#hasMoved || !touch) return;

    this.#handleDoubleTap(event, touch);
  };

  #startPinch(touchA, touchB) {
    if (!touchA || !touchB) return;

    this.#isPinching = true;
    this.#isPanning = false;
    this.#hasMoved = true;
    this.#startDistance = getDistance(pointFromTouch(touchA), pointFromTouch(touchB));
    this.#startScale = this.#scale;
    this.#setSwiperTouch(false);
    this.classList.add('is-gesturing');
  }

  #processPinch(touchA, touchB) {
    if (!touchA || !touchB || !this.#startDistance) return;

    const oldScale = this.#scale;
    const midpoint = getMidpoint(touchA, touchB);
    const focalPoint = this.#getPointFromCenter(midpoint);

    this.#scale = clamp((getDistance(pointFromTouch(touchA), pointFromTouch(touchB)) / this.#startDistance) * this.#startScale, MIN_SCALE, MAX_SCALE);

    const scaleRatio = this.#scale / oldScale;
    this.#translate.x = focalPoint.x - (focalPoint.x - this.#translate.x) * scaleRatio;
    this.#translate.y = focalPoint.y - (focalPoint.y - this.#translate.y) * scaleRatio;

    this.#constrainTranslate();
    this.#applyTransform();
  }

  #startPan(touch) {
    this.#isPanning = true;
    this.#startTouch = pointFromTouch(touch);
    this.#startTranslate = { ...this.#translate };
    this.#setSwiperTouch(false);
    this.classList.add('is-gesturing');
  }

  #processPan(touch) {
    const point = pointFromTouch(touch);
    this.#translate.x = this.#startTranslate.x + point.x - this.#startTouch.x;
    this.#translate.y = this.#startTranslate.y + point.y - this.#startTouch.y;
    this.#constrainTranslate();
    this.#applyTransform();
  }

  #handleDoubleTap(event, touch) {
    const now = performance.now();
    const point = pointFromTouch(touch);
    const isDoubleTap =
      this.#lastTapPosition &&
      now - this.#lastTapTime < DOUBLE_TAP_DELAY &&
      getDistance(point, this.#lastTapPosition) < DOUBLE_TAP_DISTANCE;

    this.#lastTapTime = now;
    this.#lastTapPosition = point;

    if (!isDoubleTap) return;

    this.#captureGesture(event);
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;

    if (this.#scale > MIN_SCALE) {
      this.resetZoom();
      return;
    }

    this.#zoomToPoint(touch, 2);
  }

  #zoomToPoint(touch, targetScale) {
    const focalPoint = this.#getPointFromCenter(pointFromTouch(touch));
    const oldScale = this.#scale;

    this.#scale = clamp(targetScale, MIN_SCALE, MAX_SCALE);

    const scaleRatio = this.#scale / oldScale;
    this.#translate.x = focalPoint.x - (focalPoint.x - this.#translate.x) * scaleRatio;
    this.#translate.y = focalPoint.y - (focalPoint.y - this.#translate.y) * scaleRatio;

    this.#setSwiperTouch(false);
    this.#constrainTranslate();
    this.#applyTransform();
  }

  #captureGesture(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  #canZoom() {
    return isMobileBreakpoint() && !!this.#image;
  }

  #constrainTranslate() {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height || this.#scale <= MIN_SCALE) {
      this.#translate = { x: 0, y: 0 };
      return;
    }

    const maxX = (rect.width * this.#scale - rect.width) / 2;
    const maxY = (rect.height * this.#scale - rect.height) / 2;

    this.#translate.x = clamp(this.#translate.x, -maxX, maxX);
    this.#translate.y = clamp(this.#translate.y, -maxY, maxY);
  }

  #applyTransform() {
    const image = this.#image;
    if (!image) return;

    if (this.#scale <= MIN_SCALE) {
      image.style.transform = '';
      this.classList.remove('is-zoomed', 'is-gesturing');
      this.#setSwiperTouch(true);
      return;
    }

    image.style.transform = `matrix(${this.#scale}, 0, 0, ${this.#scale}, ${this.#translate.x}, ${this.#translate.y})`;
    this.classList.add('is-zoomed');

    if (!this.#isPinching && !this.#isPanning) {
      this.classList.remove('is-gesturing');
    }
  }

  #getPointFromCenter(point) {
    const rect = this.getBoundingClientRect();

    return {
      x: point.x - rect.left - rect.width / 2,
      y: point.y - rect.top - rect.height / 2,
    };
  }

  #setSwiperTouch(allowTouchMove) {
    const swiperElement = this.closest('.swiper');
    const swiper = swiperElement?.swiper;
    if (!swiper) return;

    if (this.#swiper !== swiper) {
      this.#swiper = swiper;
      this.#previousAllowTouchMove = swiper.allowTouchMove;
    }

    if (allowTouchMove) {
      swiper.allowTouchMove = this.#previousAllowTouchMove ?? true;
      return;
    }

    swiper.allowTouchMove = false;
  }
}

function pointFromTouch(touch) {
  return { x: touch.clientX, y: touch.clientY };
}

function getMidpoint(touchA, touchB) {
  return {
    x: (touchA.clientX + touchB.clientX) / 2,
    y: (touchA.clientY + touchB.clientY) / 2,
  };
}

function getDistance(pointA, pointB) {
  const dx = pointA.x - pointB.x;
  const dy = pointA.y - pointB.y;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('mobile-pinch-zoom')) {
  customElements.define('mobile-pinch-zoom', MobilePinchZoom);
}
